import { ChevronRight, CircleSlash2, Trophy } from "lucide-react";
import { useEffect, useRef } from "react";
import { type ComboType, type Rank, type Seat } from "@guandan/engine";
import type { SeatInfo, TableAction } from "@guandan/protocol";
import { Avatar } from "../art/Avatar.js";
import { CardFace } from "../art/Card.js";
import { groupTableActionsByTrick, type CompletedHandView } from "../game/tableHistory.js";
import { useLocale } from "../i18n.js";

type Translate = ReturnType<typeof useLocale>["t"];

function actionLabel(action: TableAction, t: Translate): string {
  if (action.kind === "passed") return t("passedAction");
  const labels: Partial<Record<ComboType, Parameters<Translate>[0]>> = {
    single: "comboSingle", pair: "comboPair", triple: "comboTriple",
    fullHouse: "comboFullHouse", straight: "comboStraight",
    consecutivePairs: "comboConsecutivePairs", consecutiveTriples: "comboConsecutiveTriples",
    straightFlush: "comboStraightFlush", bomb: "comboBomb", jokerBomb: "comboJokerBomb",
  };
  const key = action.combo === null ? undefined : labels[action.combo.type];
  return key === undefined ? t("playedCards", { count: action.combo?.cards.length ?? 0 }) : t(key);
}

function compactActionLabel(action: TableAction, t: Translate): string {
  if (action.kind === "passed") return t("pass");
  const labels: Partial<Record<ComboType, Parameters<Translate>[0]>> = {
    single: "comboCompactSingle", pair: "comboCompactPair", triple: "comboCompactTriple",
    fullHouse: "comboCompactFullHouse", straight: "comboCompactStraight",
    consecutivePairs: "comboCompactConsecutivePairs", consecutiveTriples: "comboCompactConsecutiveTriples",
    straightFlush: "comboCompactStraightFlush", bomb: "comboCompactBomb", jokerBomb: "comboCompactJokerBomb",
  };
  const key = action.combo === null ? undefined : labels[action.combo.type];
  return key === undefined ? t("playedCards", { count: action.combo?.cards.length ?? 0 }) : t(key);
}

function MiniCards({ action, level }: { action: TableAction; level: Rank }) {
  if (action.combo === null) {
    return (
      <span className="wide-history-pass-icon" aria-hidden="true">
        <CircleSlash2 />
      </span>
    );
  }
  const cards = action.combo.cards;
  return (
    <span
      className="wide-history-cards"
      aria-label={`${cards.length} cards`}
      style={{ gridTemplateColumns: `repeat(${Math.max(1, cards.length)}, minmax(0, 7px))` }}
    >
      {cards.map((card, index) => (
        <CardFace
          key={card}
          card={card}
          level={level}
          width={20}
          height={28}
          style={{ zIndex: index }}
        />
      ))}
    </span>
  );
}

function nameOf(seats: readonly SeatInfo[], seat: Seat): string {
  return seats.find((info) => info.seat === seat)?.name ?? `Seat ${seat + 1}`;
}

export function WideHistoryPanel({
  actions,
  seats,
  level,
  onOpen,
}: {
  actions: readonly TableAction[];
  seats: readonly SeatInfo[];
  level: Rank;
  onOpen: () => void;
}) {
  const { t } = useLocale();
  const listRef = useRef<HTMLOListElement | null>(null);
  const groups = groupTableActionsByTrick(actions);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [actions.length, actions.at(-1)?.seq]);
  return (
    <aside className="wide-history-panel" aria-label={t("playHistory")}>
      {actions.length === 0 ? (
        <p className="wide-panel-empty">{t("noActivity")}</p>
      ) : (
        <ol ref={listRef} className="wide-history-list">
          {groups.map((group) => (
            <li className="wide-history-trick" key={group.trick}>
              <div className="wide-history-divider"><span>{t("trickNumber", { count: group.trick })}</span></div>
              <ol className="wide-history-actions">
                {group.actions.map((action) => {
                  const info = seats.find((item) => item.seat === action.seat);
                  const spoken = `${info?.name ?? nameOf(seats, action.seat)}, ${actionLabel(action, t)}`;
                  return (
                    <li key={action.seq} className={`wide-history-entry${action.kind === "passed" ? " wide-history-pass" : ""}`} aria-label={spoken}>
                      <Avatar avatarId={info?.avatarId ?? "avatar-01"} size={30} title={spoken} />
                      <MiniCards action={action} level={level} />
                      <span className="wide-history-kind">{compactActionLabel(action, t)}</span>
                    </li>
                  );
                })}
              </ol>
            </li>
          ))}
        </ol>
      )}
      <button className="wide-panel-more" type="button" onClick={onOpen} aria-haspopup="dialog">
        {t("activityHint")} <ChevronRight aria-hidden="true" />
      </button>
    </aside>
  );
}

export function WidePlayerPanel({
  seats,
  completedHands,
}: {
  seats: readonly SeatInfo[];
  completedHands: readonly CompletedHandView[];
}) {
  const { t } = useLocale();
  const orderedSeats = [...seats].sort((a, b) => a.seat - b.seat);
  return (
    <aside className="wide-player-panel" aria-label={t("tablePlayers")}>
      <div className="wide-player-roster" aria-label={t("tablePlayers")}>
        {([0, 1] as const).map((team) => (
          <div className={`wide-team-row team-${team}`} key={team}>
            <strong>{t("team")} {team === 0 ? "A" : "B"}</strong>
            <div className="wide-team-members">
              {orderedSeats.filter((player) => player.seat % 2 === team).map((player) => (
                <span className="wide-team-member" key={player.seat} title={player.name} aria-label={`${t("team")} ${team === 0 ? "A" : "B"}, ${player.name}`}>
                  <Avatar
                    avatarId={player.avatarId}
                    size={38}
                    team={team}
                    title={`${t("team")} ${team === 0 ? "A" : "B"}, ${player.name}`}
                  />
                  <small>{player.name}</small>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <section className="wide-completed-hands" aria-labelledby="wide-completed-title">
        <header>
          <Trophy aria-hidden="true" />
          <h3 id="wide-completed-title">{t("completedHands")}</h3>
        </header>
        {completedHands.length === 0 ? (
          <p className="wide-panel-empty">{t("noCompletedHands")}</p>
        ) : (
          <ol className="wide-completed-list">
            {[...completedHands].reverse().map((hand) => (
              <li key={hand.handNumber} className="wide-completed-hand">
                <div className="wide-completed-meta">
                  <strong>{t("handNumber", { count: hand.handNumber })}</strong>
                  <span>{t("level")} {hand.level} · {t("team")} {hand.winningTeam === 0 ? "A" : "B"}</span>
                </div>
                <ol className="wide-place-list">
                  {hand.order.map((seat, index) => {
                    const info = seats.find((player) => player.seat === seat);
                    const label = `${info?.name ?? nameOf(seats, seat)}, ${t("position")} ${index + 1}`;
                    return (
                      <li key={seat} aria-label={label} title={label}>
                        <b>{index + 1}</b>
                        <Avatar avatarId={info?.avatarId ?? "avatar-01"} size={25} title={label} />
                      </li>
                    );
                  })}
                </ol>
              </li>
            ))}
          </ol>
        )}
      </section>
    </aside>
  );
}
